import React, { useCallback, useEffect, useState } from "react";
import { ThumbsUp, Share2, CornerDownRight, ChevronDown, Loader2, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/public/AuthModal";
import ReportButton from "@/components/public/ReportButton";
import { ReviewerBadge } from "@/components/games/gameMeta";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
const count = (n) => n.replies.reduce((s, r) => s + 1 + count(r), 0);

function Avatar({ c }) {
  return c.avatar ? <img src={mediaUrl(c.avatar)} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
    : <span className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-xs font-bold text-white/70 shrink-0">{(c.user_name || "?")[0].toUpperCase()}</span>;
}

function Composer({ onSubmit, placeholder = "What do you think?", autoFocus, onCancel, testid }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onSubmit(text.trim()); setText(""); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/10 focus-within:border-[hsl(var(--primary))] transition-colors" data-testid={testid}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} autoFocus={autoFocus} maxLength={1000} rows={text ? 3 : 1}
        placeholder={placeholder} data-testid={`${testid}-input`} className="w-full bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 resize-none focus:outline-none" />
      {(text || onCancel) && (
        <div className="flex justify-end gap-2 px-3 pb-3">
          {onCancel && <button onClick={onCancel} className="px-4 py-1.5 rounded-full text-xs font-bold text-white/60 hover:text-white">Cancel</button>}
          <button onClick={send} disabled={busy || !text.trim()} data-testid={`${testid}-send`} className="px-4 py-1.5 rounded-full text-xs font-bold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : "Post"}</button>
        </div>
      )}
    </div>
  );
}

function CommentNode({ c, depth, ctx }) {
  const [replying, setReplying] = useState(false);
  const [open, setOpen] = useState(depth < 1);
  const total = count(c);
  const share = () => {
    const url = `${window.location.origin}${window.location.pathname}#c-${c.id}`;
    navigator.clipboard?.writeText(url); toast.success("Link copied");
  };
  return (
    <div id={`c-${c.id}`} className="relative" data-testid={`gcomment-${c.id}`}>
      <div className="flex gap-3">
        <Avatar c={c} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white flex items-center gap-2 flex-wrap">{c.user_name} <ReviewerBadge rank={c.reviewer_rank} compact={depth > 0} /></div>
          <div className="text-[11px] text-white/40">{fmt(c.created_at)}</div>
          <p className={`mt-1.5 text-sm leading-relaxed whitespace-pre-line break-words ${c.deleted ? "italic text-white/30" : "text-white/85"}`}>{c.deleted ? "Comment deleted" : c.text}</p>
          <div className="mt-2 flex items-center gap-4 text-xs text-white/50">
            <button onClick={() => ctx.guard(() => setReplying(true))} data-testid={`gcomment-reply-${c.id}`} className="font-semibold hover:text-white">Reply</button>
            <button onClick={() => ctx.guard(() => ctx.like(c))} data-testid={`gcomment-like-${c.id}`} className={`inline-flex items-center gap-1 hover:text-white ${c.liked ? "text-[hsl(var(--primary))]" : ""}`}>
              <ThumbsUp size={13} className={c.liked ? "fill-current" : ""} /> <span data-testid={`gcomment-likes-${c.id}`}>{c.likes}</span>
            </button>
            <button onClick={share} data-testid={`gcomment-share-${c.id}`} className="inline-flex items-center gap-1 hover:text-white"><Share2 size={12} /> Share</button>
            {ctx.canDelete(c) && !c.deleted && <button onClick={() => ctx.remove(c)} data-testid={`gcomment-delete-${c.id}`} className="hover:text-red-400"><Trash2 size={12} /></button>}
            {!c.deleted && <ReportButton type="game_comment" id={c.id} ownerId={c.user_id} />}
          </div>
          {replying && <div className="mt-3"><Composer testid={`reply-box-${c.id}`} autoFocus placeholder={`Reply to ${c.user_name}…`} onCancel={() => setReplying(false)}
            onSubmit={async (t) => { await ctx.post(t, c.id); setReplying(false); setOpen(true); }} /></div>}
        </div>
      </div>
      {c.replies.length > 0 && (
        <div className={`mt-3 border-l border-white/10 space-y-4 ${depth < 3 ? "ml-3 pl-3 sm:ml-4 sm:pl-5" : "ml-0 pl-2"}`}>
          {open ? c.replies.map((r) => <ctx.Node key={r.id} c={r} depth={depth + 1} ctx={ctx} />) : (
            <button onClick={() => setOpen(true)} data-testid={`gcomment-expand-${c.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 hover:text-white">
              <CornerDownRight size={13} /> {total} {total === 1 ? "reply" : "replies"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Conversation({ gameId, onCount }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("best");
  const [auth, setAuth] = useState(false);

  const load = useCallback(() => api.get(`/gamehub/item/${gameId}/comments`, { params: { sort } })
    .then((r) => { setItems(r.data.items); setTotal(r.data.total); onCount?.(r.data.total); }).catch(() => setItems([])), [gameId, sort, onCount]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (items && window.location.hash.startsWith("#c-")) document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [items]);

  const ctx = {
    Node: CommentNode,
    guard: (fn) => (user ? fn() : setAuth(true)),
    post: async (text, parent_id) => {
      try { await api.post(`/gamehub/item/${gameId}/comments`, { text, parent_id }); await load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Could not post comment"); throw e; }
    },
    like: async (c) => {
      const r = await api.post(`/gamehub/comments/${c.id}/like`);
      const patch = (list) => list.map((n) => (n.id === c.id ? { ...n, liked: r.data.liked, likes: r.data.likes } : { ...n, replies: patch(n.replies) }));
      setItems((cur) => patch(cur));
    },
    canDelete: (c) => user && (user.user_id === c.user_id || user.is_admin),
    remove: async (c) => { if (!window.confirm("Delete this comment?")) return; await api.delete(`/gamehub/comments/${c.id}`); load(); },
  };

  return (
    <section data-testid="game-conversation">
      <div className="flex items-baseline gap-3">
        <h2 className="text-2xl font-bold text-white">Conversation</h2>
        <span className="text-sm font-bold text-white/50" data-testid="conversation-count">{total} {total === 1 ? "Comment" : "Comments"}</span>
      </div>
      <p className="mt-2 text-sm text-white/50">Have fun. Don't be mean. Feel free to criticize ideas, not people.</p>
      <div className="mt-4">
        {user ? <Composer testid="comment-box" onSubmit={(t) => ctx.post(t, null)} /> : (
          <button onClick={() => setAuth(true)} data-testid="comment-signin" className="w-full text-left px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white/40 hover:border-white/20">What do you think? <span className="text-[hsl(var(--primary))] font-semibold">Sign in to comment</span></button>
        )}
      </div>
      <div className="mt-4 relative inline-flex items-center gap-1 text-xs text-white/50">
        Sort by
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="comment-sort" className="appearance-none bg-transparent font-bold text-white pr-4 focus:outline-none cursor-pointer">
          <option value="best" className="bg-[#0d1117]">Best</option><option value="newest" className="bg-[#0d1117]">Newest</option>
        </select>
        <ChevronDown size={12} className="absolute right-0 pointer-events-none" />
      </div>
      <div className="mt-6 space-y-6">
        {items === null ? <Loader2 className="animate-spin text-white/30" /> : items.length === 0 ? (
          <div className="py-10 text-center text-white/40 text-sm" data-testid="comments-empty"><MessageCircle className="mx-auto mb-2" size={22} /> Be the first to start the conversation.</div>
        ) : items.map((c) => <div key={c.id} className="pb-6 border-b border-white/5 last:border-0"><CommentNode c={c} depth={0} ctx={ctx} /></div>)}
      </div>
      <AuthModal open={auth} onClose={() => setAuth(false)} />
    </section>
  );
}
